import React, { useEffect, useRef, useState } from 'react';
import useDemoScript from './useDemoScript.js';

/**
 * DemoStage — the frame every Knowledge-page demo lives in.
 *
 * Renders a dashed stage (`.kb-stage`), a caption bar showing the current
 * step, and progress dots. `children` is a render function receiving the
 * current step object, so the demo component only describes its scenes:
 *
 *   <DemoStage steps={STEPS} label="Posting a private comment">
 *     {(step) => <MockComposer scene={step.scene} />}
 *   </DemoStage>
 *
 * Cursor: a step with `cursor: 'post'` glides the fake cursor onto the mock
 * element marked `data-kb-target="post"` — measured from the live layout, so
 * it always lands on the control regardless of scene. `click: true` pulses
 * it. The cursor is absolutely positioned inside the stage's own stacking
 * context (`overflow: hidden`), so it can never escape over real app chrome.
 *
 * Looping pauses while the pointer is over the stage. Under
 * prefers-reduced-motion the stage parks on the final scene and the captions
 * render as a numbered list instead of animating.
 */
export default function DemoStage({ steps, label, height = 220, children }) {
  const { step, index, total, setPaused, reduced } = useDemoScript(steps);
  const stageRef = useRef(null);
  const [cursor, setCursor] = useState(null); // px within the stage

  useEffect(() => {
    if (reduced || !step?.cursor) { setCursor(null); return; }
    const stage = stageRef.current;
    // Scope to the live scene — the hidden sizer copies carry the same
    // data-kb-target attributes.
    const el = stage?.querySelector(`.kb-scene-live [data-kb-target="${step.cursor}"]`);
    if (!el) { setCursor(null); return; }
    const sr = stage.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    setCursor({
      x: er.left - sr.left + er.width / 2,
      y: er.top - sr.top + er.height / 2,
    });
  }, [step, reduced]);

  return (
    <figure className="kb-demo" role="group" aria-label={label ? `Demo: ${label}` : 'Demo'}>
      <div
        ref={stageRef}
        className="kb-stage"
        style={{ minHeight: height }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Every scene renders invisibly in the same grid cell, so the stage
            is always as tall as its tallest scene — scene changes can never
            resize it and shift the article text the reader is on. */}
        <div className="kb-stage-grid">
          {steps.map((s, i) => (
            <div key={i} className="kb-scene-sizer" aria-hidden="true">{children(s)}</div>
          ))}
          <div className="kb-scene-live">{children(step)}</div>
        </div>
        {!reduced && cursor && (
          <span className="kb-cursor" style={{ left: cursor.x, top: cursor.y }} aria-hidden="true">
            {/* keyed child remounts per step so the click pulse can retrigger
                while the parent keeps its identity (and its glide transition) */}
            <span key={index} className={`kb-cursor-dot${step?.click ? ' click' : ''}`} />
          </span>
        )}
      </div>

      {reduced ? (
        <figcaption className="kb-caption">
          <ol className="kb-caption-list">
            {steps.map((s, i) => <li key={i}>{s.caption}</li>)}
          </ol>
        </figcaption>
      ) : (
        <figcaption className="kb-caption">
          <span className="kb-caption-text">{step?.caption}</span>
          <span className="kb-progress" aria-hidden="true">
            {steps.map((_, i) => (
              <span key={i} className={`kb-progress-dot${i === index ? ' active' : ''}`} />
            ))}
          </span>
          <span className="sr-only">Step {index + 1} of {total}</span>
        </figcaption>
      )}
    </figure>
  );
}
